/**
 * Evidence Preview Dialog Component
 * Displays evidence preview in a modal dialog
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import type { MissionEvidence } from '../../../../types/mission';

interface EvidencePreviewDialogProps {
  open: boolean;
  evidence: MissionEvidence | null;
  onClose: () => void;
  onDownload?: (evidence: MissionEvidence) => void;
}

export const EvidencePreviewDialog: React.FC<EvidencePreviewDialogProps> = ({
  open,
  evidence,
  onClose,
  onDownload,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (!evidence) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleDownload = () => {
    if (onDownload && evidence) {
      onDownload(evidence);
    } else if (evidence.url) {
      window.open(evidence.url, '_blank');
    }
  };

  const renderPreview = () => {
    switch (evidence.type) {
      case 'photo':
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              maxHeight: '70vh',
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Box
              component="img"
              src={evidence.url}
              alt={evidence.fileName || 'Evidence photo'}
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                const target = e.target as HTMLImageElement;
                target.src = `https://via.placeholder.com/800x600/333333/ffffff?text=Image+Not+Available`;
              }}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          </Box>
        );

      case 'video':
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              maxHeight: '70vh',
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Box
              component="video"
              src={evidence.url}
              controls
              preload="metadata"
              onError={(e) => {
                console.error('Video failed to load:', evidence.url);
              }}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
              }}
            >
              Your browser does not support the video tag.
            </Box>
          </Box>
        );

      case 'file':
      default:
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              backgroundColor: theme.palette.background.default,
              padding: theme.spacing(4),
            }}
          >
            <Box
              sx={{
                fontSize: 80,
                color: theme.palette.text.secondary,
                marginBottom: theme.spacing(2),
              }}
            >
              <FileIcon sx={{ fontSize: 80 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(1),
                textAlign: 'center',
              }}
            >
              {evidence.fileName || 'File Preview'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                marginBottom: theme.spacing(2),
              }}
            >
              {evidence.fileSize && formatFileSize(evidence.fileSize)}
              {evidence.mimeType && ` • ${evidence.mimeType}`}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download File
            </Button>
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.customColors.tableBorder}`,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {evidence.fileName || 'Evidence Preview'}
          </Typography>
          {evidence.uploadedBy && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Uploaded by {evidence.uploadedBy} • {evidence.uploadedAtText}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        {renderPreview()}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.customColors.tableBorder}`,
          padding: theme.spacing(2),
        }}
      >
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
